import type { I18n } from "@lingui/core";
import type { PrefilledMessage } from "@mobility-item-page/dealer/client";
import type { MobilityAd } from "@mobility-item-page/types";
import { ObjectPosition } from "@mobility-item-page/utilities/client";

/** The EV info expandable a contact action belongs to */
export enum ContactTopic {
  BatteryHealth = "battery-health",
  BatteryWarranty = "battery-warranty",
}

type TopicContent = {
  /** The "ask the seller" link itself — what the buyer sees before the contact form opens */
  label: string;
  /**
   * Which EV expandable the click came from, so M_ev01 can be told apart from the main contact CTA.
   * `object.position` on dealer ads; `object.name` on private ones, where the podlet fires it.
   */
  trackingObjectPosition: ObjectPosition;
  /** The question added to the contact form, so the buyer only has to hit send */
  message: PrefilledMessage;
};

/** Opens the message when the buyer has not written anything yet, and is left out when appending */
const getGreeting = (i18n: I18n): string =>
  i18n.t({
    id: "itemPageLayout.ev.rangeAndBattery.action.greeting",
    message: "Hi, I'm interested in this car.",
    comment: "Opening line of the message prefilled in the contact form from the electric car ad links",
  });

/** Closes the message, and stays at the bottom however many questions the buyer adds */
const getSignoff = (i18n: I18n): string =>
  i18n.t({
    id: "itemPageLayout.ev.rangeAndBattery.action.signoff",
    message: "Thanks!",
    comment: "Closing line of the message prefilled in the contact form from the electric car ad links",
  });

/** Everything a topic says to the buyer, from the link they click to the question they send */
export const getTopicContent = (
  topic: ContactTopic,
  i18n: I18n,
  adViewType: MobilityAd["adViewType"],
): TopicContent => {
  const greeting = getGreeting(i18n);
  const signoff = getSignoff(i18n);

  switch (topic) {
    case ContactTopic.BatteryHealth:
      return {
        label: i18n.t({
          id: "itemPageLayout.ev.rangeAndBattery.batteryHealth.action",
          message: "Ask the seller for a battery health test",
          comment: "Link that opens the message form, in the battery health expandable on electric car ads",
        }),
        trackingObjectPosition: ObjectPosition.BatteryHealth,
        message: {
          greeting,
          signoff,
          question: i18n.t({
            id: "itemPageLayout.ev.rangeAndBattery.batteryHealth.action.message",
            message:
              "Do you have a recent battery health (State of Health) report - or could a battery test be arranged before purchase? It would help me feel confident about the battery.",
            comment: "Message prefilled in the contact form when the battery health link is used on electric car ads",
          }),
        },
      };
    case ContactTopic.BatteryWarranty:
      // New cars have their full warranty ahead of them, so asking what is left makes no sense
      return adViewType === "car-new"
        ? {
            label: i18n.t({
              id: "itemPageLayout.ev.rangeAndBattery.batteryWarranty.action.carNew",
              message: "Ask the seller about battery warranty",
              comment: "Link that opens the message form, in the battery warranty expandable on new electric car ads",
            }),
            trackingObjectPosition: ObjectPosition.BatteryWarranty,
            message: {
              greeting,
              signoff,
              question: i18n.t({
                id: "itemPageLayout.ev.rangeAndBattery.batteryWarranty.action.carNew.message",
                message: "What does the battery warranty cover, and how many years or km does it run?",
                comment:
                  "Message prefilled in the contact form when the battery warranty link is used on new electric car ads",
              }),
            },
          }
        : {
            label: i18n.t({
              id: "itemPageLayout.ev.rangeAndBattery.batteryWarranty.action",
              message: "Ask the seller about remaining coverage",
              comment: "Link that opens the message form, in the battery warranty expandable on electric car ads",
            }),
            trackingObjectPosition: ObjectPosition.BatteryWarranty,
            message: {
              greeting,
              signoff,
              question: i18n.t({
                id: "itemPageLayout.ev.rangeAndBattery.batteryWarranty.action.message",
                message:
                  "How much of the battery warranty is left - how many years or km remain, and what does it cover?",
                comment:
                  "Message prefilled in the contact form when the battery warranty link is used on electric car ads",
              }),
            },
          };
  }
};

/**
 * Reads back the `?contact=<slug>` the login and eID redirects return with. Anything else — a stale
 * link, a hand-typed URL, a plain `?contact` — is not a topic, and simply leaves the form empty.
 */
export const parseContactTopic = (slug: string | undefined): ContactTopic | undefined =>
  Object.values(ContactTopic).find((topic) => topic === slug);
